import { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useIntakeWizard } from '@/hooks/useIntakeWizard'
import type { ServiceId, BudgetRange, IntakeFormData } from '@/hooks/useIntakeWizard'
import { publicTrpc } from '@/lib/public-trpc'
import type { Need, Budget, Involvement } from '@/lib/public-trpc'
import { trackFormSubmit } from '@/lib/analytics'
import { ContactStep } from './steps/ContactStep'
import { OrganizationStep } from './steps/OrganizationStep'
import { ServicesStep } from './steps/ServicesStep'
import { BudgetStep } from './steps/BudgetStep'
import { ScheduleStep } from './steps/ScheduleStep'

interface IntakeWizardProps {
  onSuccess?: (result: { calendlyUrl?: string; selfServeUrl?: string }) => void
}

// -----------------------------------------------------------------------------
// Frontend → backend field mapping
// -----------------------------------------------------------------------------
// The backend Zod schema (app/server/public/src/trpc/routers/intake.router.ts)
// still uses the old `needs`, `budget`, `involvement`, `timeline` shape. The
// redesigned frontend collects `services` and `budgetRange` instead. This keeps
// the frontend redesign decoupled from the app server deploy pipeline — we map
// the new shape back onto the old schema at submission time.
//
// The mapping is deliberately lossy: the exact service multi-select isn't
// preserved. That's fine for v1 because Garrett proposes the fit live on the
// call, not via the form. When the app server schema catches up, we'll send
// the richer fields directly.

function mapServicesToNeeds(services: ServiceId[]): Need[] {
  const needs = new Set<Need>()
  for (const service of services) {
    switch (service) {
      case 'cms-platform':
      case 'new-website-content-ready':
      case 'new-website-with-coaching':
      case 'branding-logo':
      case 'custom-design':
      case 'ongoing-updates':
        needs.add('website')
        break
      case 'email-marketing':
        needs.add('email')
        break
      case 'social-media':
        needs.add('blog')
        break
    }
  }
  // The backend requires at least one `need`. cms-platform is always selected,
  // so `website` will always be present. Belt-and-suspenders default:
  if (needs.size === 0) needs.add('website')
  return Array.from(needs)
}

function mapBudgetRangeToBudget(range: BudgetRange): Budget {
  switch (range) {
    case '20-50':
      return 'under-50'
    case '50-150':
      return '$50-200'
    case '150-300':
      return '$200-500'
    case '300-600':
      return '$500-1000'
    case '600-plus':
      return '$1000+'
    case 'unsure':
      return 'under-50'
  }
}

function mapBudgetRangeToInvolvement(range: BudgetRange): Involvement {
  switch (range) {
    case '20-50':
    case '50-150':
      return 'self-managed'
    case '150-300':
      return 'light-support'
    case '300-600':
      return 'managed'
    case '600-plus':
      return 'full-service'
    case 'unsure':
      return 'managed'
  }
}

function buildSubmissionPayload(formData: IntakeFormData) {
  const budgetRange = formData.budgetRange ?? 'unsure'
  return {
    name: formData.name,
    email: formData.email,
    organization: formData.organization,
    website: formData.website || undefined,
    orgType: formData.orgType!,
    needs: mapServicesToNeeds(formData.services),
    involvement: mapBudgetRangeToInvolvement(budgetRange),
    budget: mapBudgetRangeToBudget(budgetRange),
    timeline: 'exploring' as const,
    subscribeNewsletter: formData.subscribeNewsletter,
    source: 'studio' as const,
  }
}

// -----------------------------------------------------------------------------
// Slide animation
// -----------------------------------------------------------------------------
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? '30%' : '-30%',
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? '-30%' : '30%',
    opacity: 0,
  }),
}

function ProgressBar({ currentStep, totalSteps }: { currentStep: number; totalSteps: number }) {
  const progress = (currentStep / totalSteps) * 100

  return (
    <div className="fixed top-0 right-0 left-0 z-50">
      <div className="bg-border h-1">
        <motion.div
          className="bg-primary h-full"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        />
      </div>
    </div>
  )
}

// -----------------------------------------------------------------------------
// IntakeWizard
// -----------------------------------------------------------------------------
export function IntakeWizard({ onSuccess }: IntakeWizardProps) {
  const wizard = useIntakeWizard()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // The Budget step is step 4. When it calls onNext, we submit the form to the
  // backend and THEN transition to the Schedule (confirmation) step. This
  // replaces the old ReviewStep submission flow.
  const submitAndAdvance = async () => {
    if (!wizard.formData.orgType || !wizard.formData.budgetRange) {
      // Shouldn't happen — Budget button is disabled until budgetRange is set
      wizard.goNext()
      return
    }
    setIsSubmitting(true)
    setSubmitError(null)

    try {
      const payload = buildSubmissionPayload(wizard.formData)
      const result = await publicTrpc.intake.submit.mutate(payload)

      trackFormSubmit('get-started', wizard.formData.email)

      const calendlyUrl = 'calendlyUrl' in result ? result.calendlyUrl : undefined
      const selfServeUrl = 'selfServeUrl' in result ? result.selfServeUrl : undefined

      wizard.completeSubmission({ calendlyUrl, selfServeUrl })
      onSuccess?.({ calendlyUrl, selfServeUrl })
    } catch (err) {
      console.error('Intake form submission failed:', err)
      setSubmitError('Something went wrong. Please try again or email hello@fuguelab.com.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderStep = () => {
    const commonProps = {
      formData: wizard.formData,
      updateField: wizard.updateField,
      onNext: wizard.goNext,
      onBack: wizard.goBack,
    }

    switch (wizard.step) {
      case 'contact':
        return <ContactStep {...commonProps} />
      case 'organization':
        return <OrganizationStep {...commonProps} />
      case 'services':
        return <ServicesStep {...commonProps} />
      case 'budget':
        return (
          <BudgetStep
            {...commonProps}
            // Override onNext so Budget → Schedule triggers submission first
            onNext={submitAndAdvance}
          />
        )
      case 'schedule':
        return <ScheduleStep formData={wizard.formData} result={wizard.submissionResult} />
      default:
        return null
    }
  }

  return (
    <div className="bg-background min-h-screen">
      <ProgressBar currentStep={wizard.currentStep} totalSteps={wizard.totalSteps} />

      <div className="flex min-h-screen items-center justify-center px-4 py-16">
        <AnimatePresence mode="wait" custom={wizard.direction}>
          <motion.div
            key={wizard.step}
            custom={wizard.direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: 'spring', stiffness: 300, damping: 30 },
              opacity: { duration: 0.15 },
            }}
            className="w-full max-w-3xl"
          >
            {isSubmitting ? (
              <div className="mx-auto max-w-xl space-y-4 text-center">
                <div className="border-primary inline-block size-8 animate-spin rounded-full border-4 border-t-transparent" />
                <p className="text-lg font-medium">Saving your info…</p>
                <p className="text-muted-foreground text-sm">This should only take a second.</p>
              </div>
            ) : (
              renderStep()
            )}
            {submitError && !isSubmitting && wizard.step === 'budget' && (
              <p className="mt-4 text-center text-sm text-red-500">{submitError}</p>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  )
}
